import SaveIcon from '@mui/icons-material/Save'
import {
  Alert,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useLazyGetUserByIdQuery,
  usePatchUserPrefixesMutation,
} from 'store/auth/auth.api'
import { setUser } from 'store/auth/auth.slice'
import { RootState } from 'store'
import { getUserId } from 'utils/common'
import TranspotsErrorSnackbar from 'components/transpots-error-snackbar/transpots-error-snackbar.component'
import useSubmitLock from 'hooks/use-submit-lock'
import CustomSettingCode from './custom-setting-code/custom-setting-code.component'
import {
  HeaderRow,
  PageContainer,
  SaveChangesButton,
} from './custom-setting.styles'

type PrefixSettings = {
  orderPrefix: string
  tripPrefix: string
  invoicePrefix: string
}

type CodeSettings = PrefixSettings & {
  orderStartNumber: string
  tripStartNumber: string
  invoiceStartNumber: string
}

type FieldErrors = Partial<Record<keyof CodeSettings, string>>

const DEFAULT_SETTINGS: CodeSettings = {
  orderPrefix: 'ORDR',
  orderStartNumber: '0000',
  tripPrefix: 'TRIP',
  tripStartNumber: '0000',
  invoicePrefix: 'INVC',
  invoiceStartNumber: '0000',
}

const padStartNumber = (value: number | string | null | undefined) => {
  const digits = String(value ?? '0').replace(/\D/g, '')
  if (digits === '') {
    return '0000'
  }
  return digits.padStart(4, '0')
}

const PREFIX_MAX_LENGTH = 10

const normalizePrefix = (value: string) =>
  value
    .replace(/-/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, PREFIX_MAX_LENGTH)

const toFormSettings = (user?: {
  orderPrefix?: string
  orderStartNumber?: number
  tripPrefix?: string
  tripStartNumber?: number
  invoicePrefix?: string
  invoiceStartNumber?: number
}): CodeSettings => ({
  orderPrefix: normalizePrefix(user?.orderPrefix || DEFAULT_SETTINGS.orderPrefix),
  orderStartNumber: padStartNumber(user?.orderStartNumber),
  tripPrefix: normalizePrefix(user?.tripPrefix || DEFAULT_SETTINGS.tripPrefix),
  tripStartNumber: padStartNumber(user?.tripStartNumber),
  invoicePrefix: normalizePrefix(
    user?.invoicePrefix || DEFAULT_SETTINGS.invoicePrefix
  ),
  invoiceStartNumber: padStartNumber(user?.invoiceStartNumber),
})

const validateSettings = (settings: CodeSettings): FieldErrors => {
  const errors: FieldErrors = {}
  ;(['orderPrefix', 'tripPrefix', 'invoicePrefix'] as const).forEach((field) => {
    const prefix = settings[field].trim()
    if (!prefix) {
      errors[field] = 'Prefix is required'
    } else if (prefix.includes('-')) {
      errors[field] = 'Prefix cannot contain -'
    } else if (prefix.length > PREFIX_MAX_LENGTH) {
      errors[field] = `Prefix must be ${PREFIX_MAX_LENGTH} characters or fewer`
    } else if (!/^[A-Z0-9]+$/.test(prefix)) {
      errors[field] = 'Prefix must be alphanumeric'
    }
  })
  ;(['orderStartNumber', 'tripStartNumber', 'invoiceStartNumber'] as const).forEach(
    (field) => {
      if (!/^\d+$/.test(settings[field])) {
        errors[field] = 'Start number is required'
      }
    }
  )
  return errors
}

const headerCellSx = {
  color: '#667085',
  fontWeight: 600,
  borderColor: '#eaecf0',
  py: 1.5,
}

const CustomSetting = () => {
  const dispatch = useDispatch()
  const userId = getUserId()
  const user = useSelector((state: RootState) => state.auth.user)

  const [settings, setSettings] = useState<CodeSettings>(DEFAULT_SETTINGS)
  const [savedSettings, setSavedSettings] =
    useState<CodeSettings>(DEFAULT_SETTINGS)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [errorMessage, setErrorMessage] = useState('')
  const [successOpen, setSuccessOpen] = useState(false)

  const [patchUserPrefixes, { isLoading }] = usePatchUserPrefixesMutation()
  const { isSubmitting, wrapClick } = useSubmitLock()
  const [getUser] = useLazyGetUserByIdQuery()

  useEffect(() => {
    if (!user?.id) {
      return
    }
    const nextSettings = toFormSettings(user)
    setSettings(nextSettings)
    setSavedSettings(nextSettings)
    setFieldErrors({})
  }, [user])

  const isDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(savedSettings),
    [settings, savedSettings]
  )

  const updatePrefix = (field: keyof PrefixSettings, value: string) => {
    setSettings((prev) => ({ ...prev, [field]: normalizePrefix(value) }))
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
  }

  const updateStartNumber = (field: keyof CodeSettings, value: string) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value.replace(/\D/g, '').slice(0, 6),
    }))
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
  }

  const padField = (field: keyof CodeSettings) => {
    setSettings((prev) => ({
      ...prev,
      [field]: padStartNumber(prev[field]),
    }))
  }

  const handleSave = async () => {
    if (!isDirty || isLoading || isSubmitting || !userId) {
      return
    }

    const errors = validateSettings(settings)
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) {
      setErrorMessage('Please enter a valid prefix and start number.')
      return
    }

    try {
      setErrorMessage('')
      const updated = await patchUserPrefixes({
        userId,
        orderPrefix: settings.orderPrefix.trim().toUpperCase(),
        orderStartNumber: Number.parseInt(settings.orderStartNumber, 10),
        tripPrefix: settings.tripPrefix.trim().toUpperCase(),
        tripStartNumber: Number.parseInt(settings.tripStartNumber, 10),
        invoicePrefix: settings.invoicePrefix.trim().toUpperCase(),
        invoiceStartNumber: Number.parseInt(settings.invoiceStartNumber, 10),
      }).unwrap()

      let nextUser = updated
      try {
        nextUser = await getUser({ userId }, false).unwrap()
      } catch {
        nextUser = updated
      }

      dispatch(setUser({ user: nextUser }))
      const nextSettings = toFormSettings(nextUser)
      setSettings(nextSettings)
      setSavedSettings(nextSettings)
      setSuccessOpen(true)
    } catch (err: any) {
      const message =
        err?.data?.error?.message ||
        err?.data?.message ||
        (err?.status && err?.error
          ? `${err.status}: ${err.error}`
          : 'Failed to update custom settings')
      setErrorMessage(message)
    }
  }

  return (
    <PageContainer>
      <HeaderRow>
        <Stack spacing={0.5}>
          <Typography variant="h5" fontWeight={700}>
            Custom Setting
          </Typography>
          <Typography color="text.secondary">
            Manage prefixes and start numbers for your document and trip codes.
          </Typography>
        </Stack>
        <SaveChangesButton
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={wrapClick(handleSave)}
          disabled={!isDirty || isLoading || isSubmitting || !userId}
          sx={{
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
        >
          {isLoading || isSubmitting ? 'Saving...' : 'Save Changes'}
        </SaveChangesButton>
      </HeaderRow>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid #eaecf0',
          borderRadius: '10px',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell sx={{ ...headerCellSx, width: '32%' }}>
                Code Type
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: '34%' }}>
                Prefix
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: '34%' }}>
                Start Number
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <CustomSettingCode
              title="Order Code"
              prefix={settings.orderPrefix}
              startNumber={settings.orderStartNumber}
              prefixError={fieldErrors.orderPrefix}
              startNumberError={fieldErrors.orderStartNumber}
              onPrefixChange={(value) => updatePrefix('orderPrefix', value)}
              onStartNumberChange={(value) =>
                updateStartNumber('orderStartNumber', value)
              }
              onStartNumberBlur={() => padField('orderStartNumber')}
            />
            <CustomSettingCode
              title="Trip Code"
              prefix={settings.tripPrefix}
              startNumber={settings.tripStartNumber}
              prefixError={fieldErrors.tripPrefix}
              startNumberError={fieldErrors.tripStartNumber}
              onPrefixChange={(value) => updatePrefix('tripPrefix', value)}
              onStartNumberChange={(value) =>
                updateStartNumber('tripStartNumber', value)
              }
              onStartNumberBlur={() => padField('tripStartNumber')}
            />
            <CustomSettingCode
              title="Invoice Code"
              prefix={settings.invoicePrefix}
              startNumber={settings.invoiceStartNumber}
              prefixError={fieldErrors.invoicePrefix}
              startNumberError={fieldErrors.invoiceStartNumber}
              onPrefixChange={(value) => updatePrefix('invoicePrefix', value)}
              onStartNumberChange={(value) =>
                updateStartNumber('invoiceStartNumber', value)
              }
              onStartNumberBlur={() => padField('invoiceStartNumber')}
            />
          </TableBody>
        </Table>
      </TableContainer>

      <TranspotsErrorSnackbar
        message={errorMessage}
        open={errorMessage !== ''}
        handleClose={() => setErrorMessage('')}
      />
      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Custom settings updated
        </Alert>
      </Snackbar>
    </PageContainer>
  )
}

export default CustomSetting
